import { useEffect, useRef, useState } from "react"
import LiteratureReviewLayout from "../../Components/LiteratureReviewLayout/LiteratureReviewLayout"
import {
    submitArticleIdsForAbstractScreening,
    getAbstractScreeningStatus,
    upsertAbstractScreeningColumn,
    deleteAbstractScreeningColumn,
} from "../../Utils/functions"
import { DoiButton } from "../../Components/DoiButton/DoiButton"
import CircularProgress from "@mui/material/CircularProgress"
import "./AbstractScreening.css"
import { DragIndicator } from "@mui/icons-material"
import { MenuBook } from "@mui/icons-material"
import Add from "@mui/icons-material/Add"
import AddNewColumnPanel from "../../Components/AddNewColumnPanel/AddNewColumnPanel"
import { useLiteratureReview } from "../../Context/LiteratureReviewContext"
import usePoll from "../../hooks/usePoll"

const AbstractScreening = () => {
    const { id, workflowData, getStepsForPage, setWorkflowData, navigateToStep } = useLiteratureReview()
    const { startPolling, stopPolling } = usePoll(2000)
    const [error, setError] = useState("")
    const [isLoading, setIsLoading] = useState(false)
    const [isEvaluating, setIsEvaluating] = useState(false)
    const [suggestedColumns, setSuggestedColumns] = useState([])
    const [articleValues, setArticleValues] = useState({})
    const [screenedArticles, setScreenedArticles] = useState([])
    const [articleUniqueIds, setArticleUniqueIds] = useState([])
    const [isScreeningSectionHidden, setIsScreeningSectionHidden] = useState(
        false
    )
    const [sidebarView, setSidebarView] = useState("default")
    const [customColumns, setCustomColumns] = useState([])
    const [editingColumn, setEditingColumn] = useState(null)
    const [question, setQuestion] = useState("")
    const completedScreeningRef = useRef(false)
    const pollGenerationRef = useRef(0)

    const steps = getStepsForPage("abstract-screening")

    const applyScreeningResult = (result) => {
        if (!result) return
        if (completedScreeningRef.current && result.status && result.status !== "completed") return
        if (result.status === "completed") completedScreeningRef.current = true
        if (result?.suggestedColumns != null) setSuggestedColumns(Array.isArray(result.suggestedColumns) ? result.suggestedColumns : [])
        if (result?.articleValues != null && typeof result.articleValues === "object") {
            setArticleValues((prev) => (
                result.status === "completed"
                    ? result.articleValues
                    : { ...prev, ...result.articleValues }
            ))
        }
        if (result?.articleUniqueIds != null) setArticleUniqueIds(result.articleUniqueIds)
        if (result?.addedArticles != null) setScreenedArticles(result.addedArticles)
        if (result?.question != null) setQuestion(result.question)
    }

    const pollUntilComplete = () => {
        const generation = ++pollGenerationRef.current
        startPolling(async (stop) => {
            try {
                const status = await getAbstractScreeningStatus(id)
                if (generation !== pollGenerationRef.current && status?.status !== "completed") return
                applyScreeningResult(status)
                if (Array.isArray(status?.articleUniqueIds) && status.articleUniqueIds.length > 0) {
                    setIsLoading(false)
                }
                if (status?.status === "completed") {
                    setIsLoading(false)
                    setIsEvaluating(false)
                    stop()
                } else if (status?.status === "failed") {
                    setError(status.error || "Failed to load screening data")
                    setIsLoading(false)
                    setIsEvaluating(false)
                    stop()
                }
            } catch (err) {
                setError(err.message || "Failed to load screening data")
                setIsLoading(false)
                setIsEvaluating(false)
                stop()
            }
        })
    }

    useEffect(() => {
        if (!id) return

        const fetchData = async () => {
            completedScreeningRef.current = false
            setIsLoading(true)
            setIsEvaluating(true)
            setError("")
            try {
                const existing = await getAbstractScreeningStatus(id)

                if (existing?.status === "completed") {
                    completedScreeningRef.current = true
                    applyScreeningResult(existing)
                    setIsLoading(false)
                    setIsEvaluating(false)
                    return
                }

                if (existing?.status === "processing") {
                    completedScreeningRef.current = false
                    applyScreeningResult(existing)
                    pollUntilComplete()
                    return
                }

                if (existing?.status === "failed") {
                    setError(existing.error || "Failed to load screening data")
                    setIsLoading(false)
                    setIsEvaluating(false)
                    return
                }

                const result = await submitArticleIdsForAbstractScreening(id)
                if (result?.status === "completed") {
                    applyScreeningResult(result)
                    setIsLoading(false)
                    setIsEvaluating(false)
                } else if (result?.status === "processing" || result?.status === "pending") {
                    completedScreeningRef.current = false
                    applyScreeningResult(result)
                    pollUntilComplete()
                } else if (result?.suggestedColumns != null) {
                    applyScreeningResult(result)
                    setIsLoading(false)
                    setIsEvaluating(false)
                } else {
                    setIsLoading(false)
                    setIsEvaluating(false)
                }
            } catch (err) {
                setError(err.message || "Failed to load screening data")
                setIsLoading(false)
                setIsEvaluating(false)
            }
        }
        fetchData()

        return stopPolling
    }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleBack = () => {
        navigateToStep("paper-sources")
    }

    const handleNext = () => {
        // suggestedColumns already includes user-added columns from the API; only append custom-only rows not yet in that list.
        const suggestedIds = new Set(suggestedColumns.map((c) => c?.id).filter(Boolean))
        const extraFromCustom = customColumns
            .filter((c) => c?.id && !suggestedIds.has(c.id))
            .map((c) => ({
                id: c.id,
                label: c.name,
                screeningQuestion: c.screeningQuestion,
            }))
        const mergedSuggestedColumns = [...suggestedColumns, ...extraFromCustom].filter(
            (col, idx, arr) => col?.id && arr.findIndex((c) => c?.id === col.id) === idx
        )
        setWorkflowData({
            screenedArticles,
            articleUniqueIds,
            articleValues,
            suggestedColumns: mergedSuggestedColumns,
        })
        navigateToStep("recommended-abstracts")
    }

    const handleAddCustomColumn = async ({ name, screeningQuestion }) => {
        if (!id) throw new Error("Literature review job id is missing")
        const result = await upsertAbstractScreeningColumn(id, {
            columnName: name,
            screeningQuestion
        })
        const newColumn = result?.newColumn ?? result?.column
        if (!newColumn?.id) throw new Error("Server did not return newColumn.id")

        const customEntry = {
            id: newColumn.id,
            name: newColumn.label ?? name,
            screeningQuestion: newColumn.screeningQuestion ?? screeningQuestion,
        }
        if (Array.isArray(result?.suggestedColumns)) {
            setSuggestedColumns(result.suggestedColumns)
        }
        setCustomColumns((prev) => [customEntry, ...prev.filter((c) => c.id !== newColumn.id)])
        if (result?.articleValues != null && typeof result.articleValues === "object" && !Array.isArray(result.articleValues)) {
            setArticleValues(result.articleValues)
        }
    }

    const handleUpdateCustomColumn = async ({ columnId, name, screeningQuestion }) => {
        if (!id) throw new Error("Literature review job id is missing")
        const result = await upsertAbstractScreeningColumn(id, {
            columnName: name,
            screeningQuestion,
            columnId
        })

        if (Array.isArray(result?.suggestedColumns)) {
            setSuggestedColumns(result.suggestedColumns)
        }

        setCustomColumns((prev) =>
            prev.map((c) =>
                c?.id === columnId
                    ? {
                        ...c,
                        name,
                        screeningQuestion,
                    }
                    : c
            )
        )

        if (result?.articleValues != null && typeof result.articleValues === "object" && !Array.isArray(result.articleValues)) {
            setArticleValues(result.articleValues)
        }
    }

    const handleDeleteCustomColumn = async (columnId) => {
        if (!id) throw new Error("Literature review job id is missing")
        if (!columnId) throw new Error("Column id is missing")

        const result = await deleteAbstractScreeningColumn(id, columnId)

        setSuggestedColumns(result.suggestedColumns ?? [])
        setArticleValues(result.articleValues ?? {})
        setCustomColumns((prev) => prev.filter((c) => c?.id !== columnId))
        setEditingColumn((prev) => (prev?.id === columnId ? null : prev))
    }

    const openAddColumnPanel = () => {
        setEditingColumn(null)
        setSidebarView("add-column")
    }

    const openEditColumnPanel = (col) => {
        if (!col?.id) return
        setEditingColumn({
            id: col.id,
            name: col.name ?? col.label ?? "",
            screeningQuestion: col.screeningQuestion ?? "",
        })
        setSidebarView("add-column")
    }

    const closeColumnPanel = () => {
        setSidebarView("default")
        setEditingColumn(null)
    }

    const formatColumnLabel = (key) =>
        (key || "")
            .split(/[-_]/)
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
            .join(" ")

    const getValuesForArticle = (articleId) => {
        if (articleId == null) return {}
        const article =
            articleValues[articleId] ??
            articleValues[String(articleId)] ??
            articleValues[Number(articleId)]
        if (article == null || Array.isArray(article)) return {}

        // New structure: { inclusion_scores: {...}, columns: { col_id: { value, rationale } } }
        if (article.columns && typeof article.columns === "object") {
            const flat = {}
            for (const [key, col] of Object.entries(article.columns)) {
                flat[key] = col?.value ?? col
            }
            return flat
        }

        // Old structure: { col_id: "Yes"|"No"|"Maybe" }
        return article
    }

    const customById = new Map(customColumns.map((c) => [c.id, c]))
    const suggestedById = new Map(suggestedColumns.map((c) => [c.id, c]))

    const suggestedColumnDefs = suggestedColumns
        .map((c) => {
            const colId = c?.id ?? ""
            if (!colId) return null
            const custom = customById.get(colId)
            const label = custom?.name ?? c?.label ?? formatColumnLabel(colId)
            const screeningQuestion = custom?.screeningQuestion ?? c?.screeningQuestion ?? c?.question ?? ""
            return { id: colId, label, name: label, screeningQuestion }
        })
        .filter(Boolean)

    const suggestedColumnIdSet = new Set(suggestedColumns.map((c) => c?.id).filter(Boolean))
    const customColumnsNotYetInSuggested = customColumns.filter((c) => c?.id && !suggestedColumnIdSet.has(c.id))
    const customOnlyColumnDefs = customColumnsNotYetInSuggested.map((c) => ({
        id: c.id,
        label: c.name,
        name: c.name,
        screeningQuestion: c.screeningQuestion ?? "",
    }))

    const columnDefs = [...suggestedColumnDefs, ...customOnlyColumnDefs]

    const hasResults =
        !error &&
        (articleUniqueIds.length > 0 || Object.keys(articleValues).length > 0)

    const mainContent = (
        <div className="abstract-screening-content">
            {isLoading && !hasResults && (
                <div className="abstract-screening-loading">
                    <CircularProgress sx={{ color: "#0d9488" }} size={40} />
                    <p className="abstract-screening-loading-message">Evaluating</p>
                </div>
            )}
            {!isLoading && error && (
                <div className="abstract-screening-error">
                    <p className="source-card-status">{error}</p>
                </div>
            )}
            {hasResults && isEvaluating && (
                <div className="abstract-screening-evaluating-banner">
                    <CircularProgress sx={{ color: "#0d9488" }} size={16} />
                    <span>Evaluating remaining articles…</span>
                </div>
            )}
            {hasResults && (
                <>
                    <div className="abstract-screening-table-wrapper">
                        <table className="abstract-screening-table">
                            <thead>
                                <tr>
                                    <th className="abstract-screening-paper-col">Paper</th>
                                    {columnDefs.map((col) => (
                                        <th
                                            key={col.id}
                                            onClick={() => openEditColumnPanel(col)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault()
                                                    openEditColumnPanel(col)
                                                }
                                            }}
                                            className="abstract-screening-col-header--clickable"
                                        >
                                            {col.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {articleUniqueIds.map((articleId) => {
                                    const paper = screenedArticles.find((s) => String(s?.uniqueId) === String(articleId)) ?? {}
                                    const values = getValuesForArticle(articleId)
                                    const rowHasAnyValue = values && Object.keys(values).length > 0
                                    const title = paper?.title ?? ""
                                    const rawAuthors = paper?.authors
                                    const authors = Array.isArray(rawAuthors) ? rawAuthors.join(", ") : (rawAuthors ?? "")
                                    const journal = paper?.journalTitle ?? ""
                                    const publicationDate = paper?.publicationDate ?? ""
                                    const citations = paper?.citationCount ?? 0
                                    const doi = paper?.doi ?? ""
                                    return (
                                        <tr key={String(articleId)}>
                                            <td className="abstract-screening-paper-cell">
                                                <div className="abstract-screening-paper-card">
                                                    <div className="abstract-screening-paper-title">{title}</div>
                                                    {authors && <div className="abstract-screening-paper-authors">{authors}</div>}
                                                    <div className="abstract-screening-paper-meta">
                                                        <span className="paper-meta-item"> <MenuBook sx={{
                                                            fontSize: 14,
                                                            color: "#666",
                                                            mr: 0.5,
                                                            verticalAlign: "middle",
                                                        }} />{journal}, </span>
                                                        <span className="paper-meta-item">
                                                            {publicationDate},
                                                        </span> <span className="paper-meta-item">
                                                            {citations} citations,
                                                        </span>
                                                        {doi && (
                                                            <DoiButton doi={doi} />
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            {columnDefs.map((col) => {
                                                const raw = values[col.id]
                                                const isBlank = raw == null || String(raw).trim() === ""
                                                const value = isBlank ? (rowHasAnyValue ? "No" : "—") : raw
                                                const display = String(value)
                                                const lower = display.toLowerCase()
                                                const pillClass = lower === "yes" ? "pill-yes" : lower === "no" ? "pill-no" : "pill-maybe"
                                                return (
                                                    <td key={col.id}>
                                                        <span className={`abstract-screening-pill ${pillClass}`}>
                                                            <span className="abstract-screening-pill-dot" />
                                                            {display}
                                                        </span>
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    )

    const sidebarDefault = (
        <div className="abstract-screening-sidebar">
            <div className="abstract-screening-sidebar-section">
                <h3 className="abstract-screening-sidebar-title">
                    Columns{" "}
                    <span
                        className="abstract-screening-sidebar-icon"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsScreeningSectionHidden(false);
                        }}
                    >
                        ℹ️
                    </span>
                </h3>
            </div>

            {!isScreeningSectionHidden && (
                <div className="abstract-screening-sidebar-section">
                    <div className="abstract-screening-sidebar-header">
                        <h4>Add screening columns</h4>
                    </div>
                    <div className="abstract-screening-sidebar-content">
                        <p>
                            Use our suggested screening criteria or add your own. MedRPT will evaluate the sources on these criteria, then move on to screening decisions in the next step.                        </p>
                        <a
                            href="#"
                            className="abstract-screening-sidebar-link"
                            onClick={(e) => {
                                e.preventDefault();
                                setIsScreeningSectionHidden(true);
                            }}
                        >
                            Hide
                        </a>
                    </div>
                </div>
            )}
            <div className="abstract-screening-sidebar-columns-block">
                <button
                    type="button"
                    className="abstract-screening-add-new-link"
                    onClick={openAddColumnPanel}
                >
                    <Add sx={{ fontSize: 18 }} />
                    Add new column
                </button>
            </div>
            <div className="abstract-screening-sidebar-columns-block abstract-screening-sidebar-suggested-block">
                <h3 className="abstract-screening-sidebar-column-heading">
                    Screening columns
                </h3>
            </div>
            {columnDefs.length > 0 && (
                <ul className="abstract-screening-sidebar-column-list">
                    {columnDefs.map((col) => (
                        <li
                            key={col.id}
                            className="abstract-screening-sidebar-column-item abstract-screening-sidebar-column-item--clickable"
                            onClick={() => openEditColumnPanel(col)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault()
                                    openEditColumnPanel(col)
                                }
                            }}
                        >
                            <DragIndicator className="abstract-screening-sidebar-drag-icon" fontSize="small" />
                            <span>{col.label}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )

    const sidebar =
        sidebarView === "add-column" ? (
            <div className="abstract-screening-sidebar abstract-screening-sidebar--add-column">
                <AddNewColumnPanel
                    variant="screening"
                    onClose={closeColumnPanel}
                    onAdd={handleAddCustomColumn}
                    onUpdate={handleUpdateCustomColumn}
                    onDelete={handleDeleteCustomColumn}
                    editingColumn={editingColumn}
                />
            </div>
        ) : (
            sidebarDefault
        )

    return (
        <LiteratureReviewLayout
            projectTitle={workflowData.litreviewtitle || question || "Abstract screening"}
            editableTitle={!!id}
            steps={steps}
            mainContent={mainContent}
            sidebar={sidebar}
            onNext={handleNext}
            onBack={handleBack}
            nextLabel="Evaluate Screening"
            showBack={true}
            showNext={!isEvaluating && !isLoading && !error}
        />
    )
}
export default AbstractScreening